/* ═══════════════════════════════════════════════════════════════════
   CampaignWrapReport — retrospective view for accordion 5.
   ─── iter 91 ─────────────────────────────────────────────────────────
   Replaces the iter 78 CohortIntelligencePanel inside accordion 5
   ("Cohort Post-Mortem" → renamed "Campaign Wrap Report"). Designed
   to be a CONFIDENT RETROSPECTIVE, not a control panel:

     • Max 3 observation rows. Each row is a category + factual past-
       tense sentence + next-wave recommendation sentence.
     • NO buttons. NO cost figures. NO pulse deltas. NO Live Forecast
       badges. Those belong in accordion 3 (In-Flight Optimization)
       during a live wave.
     • 0% values never surface. If a factor is missing or exactly 0,
       that observation degrades gracefully or is suppressed entirely.
     • Past tense throughout — no action verbs like "now / deploy /
       swap / run". Tone is a quiet wrap-up, not a cockpit.
     • One CTA at the bottom: link to Insights & Analytics filtered to
       this wave.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, ArrowRight,
} from 'lucide-react';

// Status palette — RED only when meaningfully below target, AMBER for
// borderline, NEUTRAL when on target. Neutral is intentionally muted so
// the eye doesn't read it as a warning.
const TONE = {
  good:    { color: '#94a3b8', bg: 'rgba(148,163,184,0.06)', border: 'rgba(148,163,184,0.20)', icon: CheckCircle2 },
  warn:    { color: '#FFB020', bg: 'rgba(255,176,32,0.08)',  border: 'rgba(255,176,32,0.25)',  icon: AlertTriangle },
  bad:     { color: '#FF4D6B', bg: 'rgba(255,77,107,0.08)',  border: 'rgba(255,77,107,0.28)',  icon: AlertTriangle },
};

/**
 * Build at most 3 observations for the wrap report.
 *
 * Each observation has the shape:
 *   { tone, category, happened, recommendation }
 *
 * Returns [] if the wave didn't fire or all factors are missing — the
 * parent component will then render the "no data" fallback.
 *
 * Past-tense factual + past-tense recommendation. No action verbs.
 */
function buildWrapObservations({ pulseData, totals, creators }) {
  const out = [];
  const factors = pulseData?.factors || {};
  const totalCount = (totals && Number.isFinite(totals.total_posts)) ? totals.total_posts : 0;
  const firedCount = (totals && Number.isFinite(totals.fired_posts)) ? totals.fired_posts : 0;

  // ─── Observation 1: Acceptance Rate / Cohort Density ──────────────
  // Derived from totals (fired ÷ scheduled) when available, with
  // factor.density as fallback. We never render "0%" — if both are
  // unavailable we suppress this observation entirely.
  const acceptancePct = totalCount > 0
    ? Math.round((firedCount / totalCount) * 100)
    : (Number.isFinite(factors.density) ? Math.round(factors.density * 100) : null);

  if (acceptancePct != null && acceptancePct > 0) {
    let tone, happened, recommendation;
    if (acceptancePct >= 90) {
      tone = 'good';
      happened = `Acceptance held at ${acceptancePct}% — the cohort firing rate matched the planned roster.`;
      recommendation = 'In the next wave, the same cohort sizing approach can be reused as a baseline.';
    } else if (acceptancePct >= 75) {
      tone = 'warn';
      happened = `Acceptance landed at ${acceptancePct}% — a portion of the planned roster did not fire, which trimmed the cohort density below the target window.`;
      recommendation = 'In the next wave, the brief can plan for a larger initial cohort to absorb expected decline rates, or RFPs can be sent earlier in the planning window.';
    } else {
      tone = 'bad';
      happened = `Acceptance landed at ${acceptancePct}% — a material share of the planned roster did not fire, which compressed the realized cohort density.`;
      recommendation = 'In the next wave, the brief can plan for a larger initial cohort to account for expected decline rates, or RFPs can be sent earlier in the planning window.';
    }
    out.push({ tone, category: 'Acceptance Rate', happened, recommendation });
  }

  // ─── Observation 2: Cross-Engagement ─────────────────────────────
  // From factor.cross_engagement (0..1). Skip when exactly 0 or null —
  // a 0% cross-engagement reading usually means no creator-to-creator
  // interaction was measured rather than "creators failed to engage".
  // Surfacing 0% would make the platform look broken.
  if (Number.isFinite(factors.cross_engagement) && factors.cross_engagement > 0) {
    const pct = Math.round(factors.cross_engagement * 100);
    let tone, happened, recommendation;
    if (pct >= 80) {
      tone = 'good';
      happened = `Cross-engagement reached ${pct}% — creators amplified each other's posts in conversation, which is the signal platform algorithms reward.`;
      recommendation = 'In the next wave, the same reply-prompt structure in the brief can be carried forward.';
    } else {
      tone = pct >= 60 ? 'warn' : 'bad';
      happened = `Cross-engagement landed at ${pct}% — creators posted in parallel rather than in conversation with each other.`;
      recommendation = 'In the next wave brief, creators can be explicitly sequenced with reply prompts built into the RFP so the cohort converses rather than broadcasts.';
    }
    out.push({ tone, category: 'Cross-Engagement', happened, recommendation });
  }

  // ─── Observation 3: Creator Performance vs Predicted ─────────────
  // Compare each creator's realized burst score to the cohort median;
  // surface underperformers when present, or a neutral "no material
  // variance" line when the cohort tracked predictions. Per-creator
  // detail lives in Insights & Analytics — this section never names
  // creators publicly. Suppressed only when there isn't enough scored
  // creator data to compute a sensible signal.
  const ranked = (creators || [])
    .filter(c => Number.isFinite(c.kpi_hit_probability?.overall ?? c.burst_score))
    .map(c => ({
      handle: c.creator_handle || c.creator_name || 'creator',
      score: (c.kpi_hit_probability?.overall ?? c.burst_score ?? 0),
    }))
    .sort((a, b) => b.score - a.score);

  if (ranked.length >= 3) {
    const median = ranked[Math.floor(ranked.length / 2)].score;
    const under = ranked.filter(r => r.score > 0 && r.score < Math.max(55, median - 15));
    if (under.length > 0) {
      const n = under.length;
      out.push({
        tone: under.length >= 3 ? 'bad' : 'warn',
        category: 'Creator Performance',
        happened: n === 1
          ? `One creator landed materially below their predicted burst power, which softened the cohort's overall lift contribution.`
          : `${n} creators landed materially below their predicted burst power, which softened the cohort's overall lift contribution.`,
        recommendation: 'Their reliability vector will be updated before the next match, and the platform will weight them down accordingly in future cohort selection.',
      });
    } else if (ranked[0].score >= 75) {
      out.push({
        tone: 'good',
        category: 'Creator Performance',
        happened: `The full cohort performed within or above their predicted burst-power band — no material underperformers were detected.`,
        recommendation: 'In the next wave, the top quartile of this cohort can be carried forward as anchor creators with confidence.',
      });
    } else {
      // Cohort tracked predictions without a standout band either way —
      // surface a neutral retrospective so the section still reads as
      // a complete three-line wrap when data is intact.
      out.push({
        tone: 'good',
        category: 'Creator Performance',
        happened: `Cohort-level performance landed within the predicted burst-power band — no material variance was detected at the creator level.`,
        recommendation: 'The reliability vectors used to seed this cohort will be carried into the next match without revision.',
      });
    }
  }

  // Hard ceiling — never more than 3 observations.
  return out.slice(0, 3);
}


export default function CampaignWrapReport({
  pulseData,       // GET /pulse response — { factors, pulse_score, phase, ... }
  totals,          // liveState.totals — { fired_posts, total_posts, ... }
  creators = [],   // scores response — [{ creator_handle, kpi_hit_probability, burst_score, ... }]
  waveId,          // string — used to deep-link into Insights & Analytics
}) {
  const observations = useMemo(
    () => buildWrapObservations({ pulseData, totals, creators }),
    [pulseData, totals, creators],
  );

  const empty = observations.length === 0;
  const insightsHref = waveId ? `/insights?wave=${waveId}` : '/insights';

  return (
    <section
      data-testid="campaign-wrap-report"
      data-observation-count={observations.length}
      className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden"
    >
      {/* Header — quiet retrospective. Right side shows a read-only
          count, not a control. */}
      <header
        className="px-5 py-3 border-b border-[#1f1f1f] flex items-center justify-between flex-wrap gap-2"
        style={{ background: 'linear-gradient(135deg, rgba(168,139,250,0.05), rgba(255,255,255,0.02))' }}
      >
        <div className="flex items-center gap-2">
          <p
            className="text-[10px] uppercase tracking-[0.25em] font-black"
            style={{ color: '#A78BFA' }}
          >
            Campaign Wrap · What we observed
          </p>
        </div>
        <p
          className="text-[10px] text-gray-500"
          data-testid="wrap-observation-count"
        >
          Apply to next wave · {observations.length} observation{observations.length === 1 ? '' : 's'}
        </p>
      </header>

      {/* Body — either 3 observation rows OR a graceful no-data state.
          Never renders "0%" or naked zeros — see buildWrapObservations
          guard rails. */}
      {empty ? (
        <div
          className="px-5 py-8 text-center"
          data-testid="wrap-empty-state"
        >
          <p className="text-[12.5px] text-gray-300 leading-relaxed">
            Detailed wave-level signal wasn't captured for this campaign — the
            full report has the raw event-level data when you're ready.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#1a1a1a]">
          {observations.map((obs, i) => {
            const tone = TONE[obs.tone] || TONE.good;
            const ToneIcon = tone.icon;
            return (
              <div
                key={`${obs.category}-${i}`}
                className="flex items-start gap-3 px-5 py-4"
                data-testid={`wrap-observation-${obs.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                data-tone={obs.tone}
              >
                {/* Tone indicator — color-coded by performance band. */}
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    background: tone.bg,
                    border: `1px solid ${tone.border}`,
                  }}
                >
                  <ToneIcon className="h-3.5 w-3.5" style={{ color: tone.color }} />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Category label — colored by tone */}
                  <p
                    className="text-[10px] uppercase tracking-[0.18em] font-black mb-1"
                    style={{ color: tone.color }}
                  >
                    {obs.category}
                  </p>
                  {/* Factual past-tense sentence — what happened. */}
                  <p
                    className="text-[12.5px] text-white leading-relaxed mb-1.5"
                    data-testid="wrap-observation-happened"
                  >
                    {obs.happened}
                  </p>
                  {/* Next-wave recommendation — what to do differently
                      next time. Framed in passive voice / future-conditional
                      so it never reads as a "do this now" instruction. */}
                  <p
                    className="text-[11.5px] text-gray-400 leading-relaxed"
                    data-testid="wrap-observation-recommendation"
                  >
                    {obs.recommendation}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Single CTA — the only interactive element in the section.
          Read as a soft handoff, not a control. */}
      <div className="px-5 py-4 border-t border-[#1a1a1a]">
        <Link
          to={insightsHref}
          data-testid="wrap-view-full-report"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-bold transition-colors"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border:     '1px solid rgba(255,255,255,0.10)',
            color:      '#E5E7EB',
          }}
        >
          <span>View full wrap report in Insights &amp; Analytics</span>
          <ArrowRight className="h-3.5 w-3.5 opacity-70" />
        </Link>
      </div>

      {/* Microcopy — quiet reassurance that the platform is learning
          without requiring the brand to action anything manually. */}
      <footer className="px-5 py-2.5 border-t border-[#1a1a1a] bg-[#0a0a0a]">
        <p
          className="text-[10px] text-gray-500 leading-snug text-center"
          data-testid="wrap-learning-microcopy"
        >
          Learnings are automatically applied to your next wave brief.
        </p>
      </footer>
    </section>
  );
}
